/**
 * GDPR Cookie Consent Module
 *
 * Manages cookie consent preferences with a modal UI.
 */

const COOKIE_PREFIX = 'gdpr';
const COOKIE_EXPIRY_DAYS = 365;
const ACCEPT_VALUE = 'accepted';
const FALSE_VALUE = 'false';

// Cookie utilities
const createCookie = (name, value, days) => {
    let expires = '';
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/; SameSite=Lax';
};

const readCookie = (name) => {
    const nameEQ = name + '=';
    const cookieArray = document.cookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
        let c = cookieArray[i].trim();
        if (c.indexOf(nameEQ) === 0) {
            return c.substring(nameEQ.length, c.length);
        }
    }
    return null;
};

// Permission definitions
const permissions = {
    statistics: {
        name: 'statistics',
        cookieName: `${COOKIE_PREFIX}-statistics`,
        required: false,
        value: null
    },
    marketing: {
        name: 'marketing',
        cookieName: `${COOKIE_PREFIX}-marketing`,
        required: false,
        value: null
    }
};

// State
let wasAccepted = null;
let currentView = 'basic'; // 'basic' | 'extended' | 'hidden'

// Initialize dataLayer for GTM
window.dataLayer = window.dataLayer || [];

// Fire GTM consent events based on current consent state
const fireConsentEvents = () => {
    // Functional is always on
    window.dataLayer.push({ event: 'cmplz_event_functional' });

    // Fire statistics event if accepted
    if (readCookie(`${COOKIE_PREFIX}-statistics`) === ACCEPT_VALUE) {
        window.dataLayer.push({ event: 'cmplz_event_statistics' });
    }

    // Fire marketing event if accepted
    if (readCookie(`${COOKIE_PREFIX}-marketing`) === ACCEPT_VALUE) {
        window.dataLayer.push({ event: 'cmplz_event_marketing' });
    }
};

// Text content
const content = {
    logoUrl: '/assets/logo.png',
    logoAlt: 'Makimo',
    intro: 'We use cookies on this site to enhance your user experience. With that purpose, we gather traffic and analytics data that helps us improve the site even more.',
    privacyLink: '/privacy-policy/',
    privacyLinkText: 'Privacy and cookie policy',
    categories: {
        functional: {
            title: 'Functional',
            description: 'The technical storage or access is strictly necessary for the legitimate purpose of enabling the use of a specific service explicitly requested by the subscriber or user, or for the sole purpose of carrying out the transmission of a communication over an electronic communications network.',
            required: true
        },
        statistics: {
            title: 'Statistics',
            description: 'The technical storage or access that is used exclusively for statistical purposes.',
            required: false
        },
        marketing: {
            title: 'Marketing',
            description: 'The technical storage or access is required to create user profiles to send advertising, or to track the user on a website or across several websites for similar marketing purposes.',
            required: false
        }
    },
    buttons: {
        accept: 'Accept all',
        reject: 'Reject all',
        settings: 'Cookie settings',
        saveAndClose: 'Save and close',
        acceptAll: 'Accept all'
    }
};

// Initialize state from cookies
const initializeState = () => {
    wasAccepted = readCookie(COOKIE_PREFIX);
    Object.keys(permissions).forEach(name => {
        permissions[name].value = readCookie(permissions[name].cookieName);
    });
};

// Check if modal should be shown
const shouldShowModal = () => wasAccepted !== ACCEPT_VALUE;

// Set all permissions to accepted
const acceptAll = () => {
    Object.keys(permissions).forEach(name => {
        permissions[name].value = ACCEPT_VALUE;
        createCookie(permissions[name].cookieName, ACCEPT_VALUE, COOKIE_EXPIRY_DAYS);
    });
    wasAccepted = ACCEPT_VALUE;
    createCookie(COOKIE_PREFIX, ACCEPT_VALUE, COOKIE_EXPIRY_DAYS);
    hideModal();
    window.location.reload();
};

// Reject optional permissions
const rejectAll = () => {
    Object.keys(permissions).forEach(name => {
        if (!permissions[name].required) {
            permissions[name].value = FALSE_VALUE;
            createCookie(permissions[name].cookieName, FALSE_VALUE, COOKIE_EXPIRY_DAYS);
        }
    });
    wasAccepted = ACCEPT_VALUE;
    createCookie(COOKIE_PREFIX, ACCEPT_VALUE, COOKIE_EXPIRY_DAYS);
    hideModal();
    window.location.reload();
};

// Save current permission settings
const saveSettings = () => {
    Object.keys(permissions).forEach(name => {
        createCookie(permissions[name].cookieName, permissions[name].value || FALSE_VALUE, COOKIE_EXPIRY_DAYS);
    });
    wasAccepted = ACCEPT_VALUE;
    createCookie(COOKIE_PREFIX, ACCEPT_VALUE, COOKIE_EXPIRY_DAYS);
    hideModal();
    window.location.reload();
};

// Toggle a permission
const togglePermission = (name) => {
    if (permissions[name] && !permissions[name].required) {
        permissions[name].value = permissions[name].value === ACCEPT_VALUE ? FALSE_VALUE : ACCEPT_VALUE;
        updateToggleUI(name);
    }
};

// Update toggle button UI
const updateToggleUI = (name) => {
    const toggle = document.querySelector(`[data-gdpr-toggle="${name}"]`);
    if (toggle) {
        const isActive = permissions[name].value === ACCEPT_VALUE;
        toggle.classList.toggle('active', isActive);
        toggle.setAttribute('aria-pressed', isActive);
    }
};

// Create the modal HTML
const createModalHTML = () => {
    return `
        <div class="gdpr-consent-overlay" id="gdpr-consent-overlay">
            <div class="gdpr-consent-modal" role="dialog" aria-labelledby="gdpr-title" aria-modal="true">
                <!-- Basic View -->
                <div class="gdpr-consent-basic" id="gdpr-basic-view">
                    <div class="gdpr-consent-logo">
                        <img src="${content.logoUrl}" alt="${content.logoAlt}" width="100">
                    </div>
                    <p>${content.intro}</p>
                    <div class="gdpr-consent-actions">
                        <button type="button" class="gdpr-btn gdpr-btn-link" id="gdpr-open-settings">${content.buttons.settings}</button>
                    </div>
                    <div class="gdpr-consent-actions gdpr-consent-actions-main">
                        <button type="button" class="gdpr-btn gdpr-btn-secondary" id="gdpr-reject-all">${content.buttons.reject}</button>
                        <button type="button" class="gdpr-btn gdpr-btn-primary" id="gdpr-accept-all">${content.buttons.accept}</button>
                    </div>
                    <div class="gdpr-consent-privacy-link">
                        <a href="${content.privacyLink}">${content.privacyLinkText}</a>
                    </div>
                </div>

                <!-- Extended View -->
                <div class="gdpr-consent-extended" id="gdpr-extended-view" style="display: none;">
                    <header class="gdpr-consent-header">
                        <div class="gdpr-consent-logo">
                            <img src="${content.logoUrl}" alt="${content.logoAlt}" width="100">
                        </div>
                        <p>You can change your cookie settings at any time using the panel below.</p>
                    </header>
                    <div class="gdpr-consent-toggles">
                        <!-- Functional (always on) -->
                        <div class="gdpr-consent-category">
                            <div class="gdpr-consent-category-header">
                                <div class="gdpr-consent-category-info">
                                    <h3>${content.categories.functional.title} <span class="gdpr-always-on">(always on)</span></h3>
                                    <p>${content.categories.functional.description}</p>
                                </div>
                                <div class="gdpr-consent-toggle-wrap">
                                    <button type="button"
                                        class="gdpr-toggle active"
                                        aria-pressed="true"
                                        disabled>
                                        <span class="gdpr-toggle-handle"></span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Statistics -->
                        <div class="gdpr-consent-category">
                            <div class="gdpr-consent-category-header">
                                <div class="gdpr-consent-category-info">
                                    <h3>${content.categories.statistics.title}</h3>
                                    <p>${content.categories.statistics.description}</p>
                                </div>
                                <div class="gdpr-consent-toggle-wrap">
                                    <button type="button"
                                        class="gdpr-toggle ${permissions.statistics.value === ACCEPT_VALUE ? 'active' : ''}"
                                        data-gdpr-toggle="statistics"
                                        aria-pressed="${permissions.statistics.value === ACCEPT_VALUE}">
                                        <span class="gdpr-toggle-handle"></span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Marketing -->
                        <div class="gdpr-consent-category">
                            <div class="gdpr-consent-category-header">
                                <div class="gdpr-consent-category-info">
                                    <h3>${content.categories.marketing.title}</h3>
                                    <p>${content.categories.marketing.description}</p>
                                </div>
                                <div class="gdpr-consent-toggle-wrap">
                                    <button type="button"
                                        class="gdpr-toggle ${permissions.marketing.value === ACCEPT_VALUE ? 'active' : ''}"
                                        data-gdpr-toggle="marketing"
                                        aria-pressed="${permissions.marketing.value === ACCEPT_VALUE}">
                                        <span class="gdpr-toggle-handle"></span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="gdpr-consent-footer">
                        <button type="button" class="gdpr-btn gdpr-btn-secondary" id="gdpr-save-settings">${content.buttons.saveAndClose}</button>
                        <div class="gdpr-consent-footer-bottom">
                            <button type="button" class="gdpr-btn gdpr-btn-primary" id="gdpr-accept-all-extended">${content.buttons.acceptAll}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
};

// Show basic view
const showBasicView = () => {
    const basicView = document.getElementById('gdpr-basic-view');
    const extendedView = document.getElementById('gdpr-extended-view');
    if (basicView) basicView.style.display = 'block';
    if (extendedView) extendedView.style.display = 'none';
    currentView = 'basic';
};

// Show extended view
const showExtendedView = () => {
    const basicView = document.getElementById('gdpr-basic-view');
    const extendedView = document.getElementById('gdpr-extended-view');
    if (basicView) basicView.style.display = 'none';
    if (extendedView) extendedView.style.display = 'block';

    // Update toggle states
    Object.keys(permissions).forEach(name => updateToggleUI(name));
    currentView = 'extended';
};

// Show the modal
const showModal = () => {
    let overlay = document.getElementById('gdpr-consent-overlay');

    if (!overlay) {
        const container = document.createElement('div');
        container.innerHTML = createModalHTML();
        document.body.appendChild(container.firstElementChild);
        overlay = document.getElementById('gdpr-consent-overlay');
        attachEventListeners();
    }

    overlay.style.display = 'flex';
    showBasicView();
    currentView = 'basic';

    // Prevent body scroll
    document.body.style.overflow = 'hidden';
};

// Hide the modal
const hideModal = () => {
    const overlay = document.getElementById('gdpr-consent-overlay');
    if (overlay) {
        overlay.style.display = 'none';
    }
    currentView = 'hidden';

    // Restore body scroll
    document.body.style.overflow = '';
};

// Attach event listeners to modal buttons
const attachEventListeners = () => {
    // Accept all (basic view)
    document.getElementById('gdpr-accept-all')?.addEventListener('click', acceptAll);

    // Reject all
    document.getElementById('gdpr-reject-all')?.addEventListener('click', rejectAll);

    // Open settings
    document.getElementById('gdpr-open-settings')?.addEventListener('click', showExtendedView);

    // Save settings (extended view)
    document.getElementById('gdpr-save-settings')?.addEventListener('click', saveSettings);

    // Accept all (extended view)
    document.getElementById('gdpr-accept-all-extended')?.addEventListener('click', acceptAll);

    // Toggle buttons
    document.querySelectorAll('[data-gdpr-toggle]').forEach(toggle => {
        toggle.addEventListener('click', (e) => {
            const name = e.currentTarget.getAttribute('data-gdpr-toggle');
            togglePermission(name);
        });
    });

    // Close on overlay click (optional - uncomment if desired)
    // document.getElementById('gdpr-consent-overlay')?.addEventListener('click', (e) => {
    //     if (e.target.id === 'gdpr-consent-overlay') {
    //         hideModal();
    //     }
    // });
};

// Open modal for managing consent (used by privacy policy page)
const openManageConsent = () => {
    showModal();
    showExtendedView();
};

// Check if a specific permission is accepted
const isConsentAccepted = (name) => {
    return readCookie(permissions[name]?.cookieName) === ACCEPT_VALUE;
};

// Initialize the module
const init = () => {
    initializeState();

    if (shouldShowModal()) {
        showModal();
    } else {
        // Fire consent events for returning visitors who already gave consent
        fireConsentEvents();
    }

    // Expose global function for managing consent
    window.openGdprConsent = openManageConsent;

    // Set up manage consent buttons
    document.querySelectorAll('[data-gdpr-manage]').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            openManageConsent();
        });
    });
};

export default init;
export { isConsentAccepted, openManageConsent };
